import React from 'react';
import { IntentProps, useFullTheme } from '~/design';
import { checkIsBadgeAllowed } from '~/utils/badge';
import { BadgeContext, SrOnly } from '~/internal';
import { useId } from '~/utils';

const LABEL_CHARACTER_CAP = 4;

const badgeSize = {
  md: {
    size: 24,
    offsetDelta: 3,
  },
  sm: {
    size: 16,
    offsetDelta: 4,
  },
  xs: {
    size: 12,
    offsetDelta: 5,
  },
};

export interface BadgeProps extends IntentProps {
  /** The text that is announced to screen readers. Must sufficiently describe the badge. */
  'aria-label': string;

  /** An element that will serve as the anchor for the badge. */
  children: React.ReactNode;

  /** Badge label. Maximum of 4 characters. */
  label?: string | number;

  /** Size of badge. When 'xs' or 'sm', it shows just an indicator dot, with no label. Defaults to `md` */
  size?: 'xs' | 'sm' | 'md';

  /** If false, the badge will not show up. (default: true). */
  enabled?: boolean;
}

/**
 * Badge shows notifications, counts, or statuses over navigation elements. They are static and
 * not interactive, so they must be attached to an interactive element.
 *
 * ## Accessibility ♿
 * - Accessibility features:
 *   - Screen readers will announce a prompt indicating that certain items require attention.
 */
export function Badge({
  'aria-label': ariaLabel,
  children,
  intent = 'negative',
  label,
  size = 'md',
  enabled = true,
}: BadgeProps) {
  const { borderRadiuses, colors, typography } = useFullTheme();

  if (!ariaLabel) {
    throw new Error('aria-label is required');
  }

  if (size === 'md' && !label) {
    throw new Error('Label must be provided when Badge size is `md`');
  }

  if (label && label.toString().length > LABEL_CHARACTER_CAP) {
    throw new Error(`Label length must not exceed ${LABEL_CHARACTER_CAP} characters`);
  }

  if (!checkIsBadgeAllowed(children)) {
    throw new Error('`Badge` children must be interactive for accessibility reasons');
  }

  const badgeOffset = children ? (badgeSize[size].size / badgeSize[size].offsetDelta) * -1 : 0;
  const badgePadding = label && size === 'md' ? '0 6px' : '0';
  const badgeId = useId();

  return (
    <BadgeContext.Provider value={{ ariaDescribedBy: badgeId }}>
      <div
        css={{
          display: 'inline-block',
          minHeight: badgeSize[size].size,
          minWidth: badgeSize[size].size,
          position: 'relative',
          width: 'fit-content',
        }}
      >
        {children}
        {enabled && (
          <>
            <SrOnly id={badgeId}>{ariaLabel}</SrOnly>
            <div
              css={{
                alignItems: 'center',
                backgroundColor: {
                  magic: colors.actionMagic,
                  negative: colors.actionNegative,
                  positive: colors.actionPositive,
                  primary: colors.actionPrimary,
                  secondary: colors.actionSecondary,
                  warning: colors.actionWarning,
                }[intent],
                border: `2px solid ${colors.borderInverted}`,
                borderRadius: borderRadiuses.circle,
                color: colors.textOnAction,
                display: 'flex',
                fontFamily: typography.titleS.fontFamily,
                fontSize: '0.75rem',
                fontWeight: typography.titleS.fontWeight,
                height: badgeSize[size].size,
                justifyContent: 'center',
                minWidth: badgeSize[size].size,
                padding: badgePadding,
                pointerEvents: 'none',
                position: 'absolute',
                right: badgeOffset,
                top: badgeOffset,
              }}
              role="status"
            >
              {size === 'md' && label}
            </div>
          </>
        )}
      </div>
    </BadgeContext.Provider>
  );
}
